import { atom } from "jotai";

import { atomFamily } from "jotai/utils";
import {
  Tuple,
  Team,
  TeamWithPermission,
  getSelectedMembersOrTeamsWithPermission,
  getUniqueSelectedTeamIds,
} from "@zapier/permissions";
import { permissionsAtom } from "./fetchPermissionsSetterAtomFamily";
import { Account } from "../types";
import getCDNImageUrl from "../../../canvas/src/utils/getCDNImageUrl";

type Params = {
  currentAccountId: number | undefined;
  /* Account has multiple users */
  allowTeams: boolean;
  /* Account has multiple teams. You'll get a 403 when fetching /teams if this is false.
  See Slack thread: https://zapier.slack.com/archives/C024Z8BHJ/p1713208555005719?thread_ts=1712958519.463149&cid=C024Z8BHJ */
  hasMultipleTeams: boolean;
  account: Account | undefined;
};

export const selectedTeamsAtom = atom<TeamWithPermission[]>([]);
selectedTeamsAtom.debugLabel = "selectedTeamsAtom";

export const isFetchingSelectedTeamsAtom = atom(false);
isFetchingSelectedTeamsAtom.debugLabel = "isFetchingSelectedTeamsAtom";

export const fetchSelectedTeamsSetterAtomFamily = atomFamily(
  ({ currentAccountId, allowTeams, hasMultipleTeams, account }: Params) => {
    const fetchAtom = atom(null, async (get, set) => {
      if (!allowTeams || !hasMultipleTeams) {
        return;
      }

      set(isFetchingSelectedTeamsAtom, true);

      const permissions = get(permissionsAtom) as Tuple[];

      // Check for account admin permission
      const adminPermission = permissions.find((p) => {
        const adminPattern = /^account:(\d+)#admin$/;
        return p.object.includes("canvas") && adminPattern.test(p.user);
      });

      const uniqueTeamIds = getUniqueSelectedTeamIds(permissions, "canvas");

      if (uniqueTeamIds.length === 0 && !adminPermission) {
        set(selectedTeamsAtom, []);
        set(isFetchingSelectedTeamsAtom, false);
        return;
      }

      let selectedTeams: TeamWithPermission[] = [];

      // Fetch regular teams if there are any
      if (uniqueTeamIds.length > 0 && currentAccountId) {
        const teamsRes = await fetch(
          `/api/v4/accounts/${currentAccountId}/teams?ids=${uniqueTeamIds.join(
            ",",
          )}&limit=9000`,
        );

        if (!teamsRes.ok) {
          set(isFetchingSelectedTeamsAtom, false);
          throw new Error(`Error fetching selected teams: ${teamsRes.status}`);
        }

        const json = await teamsRes.json();
        const selectedTeamsNoPermission: Team[] = json.results;

        selectedTeams = getSelectedMembersOrTeamsWithPermission(
          selectedTeamsNoPermission,
          permissions,
          "canvas",
          "team",
        ) as TeamWithPermission[];
      }

      // Add admin team if permission exists
      if (adminPermission && account) {
        const adminTeam: TeamWithPermission & { role: "admin" } = {
          id: -1,
          name: `Account admins at ${account.name}`,
          member_count: 0,
          logo: getCDNImageUrl("account-default-image.svg"),
          role: "admin",
          permission: adminPermission.relation as "editor" | "viewer",
        };
        selectedTeams.unshift(adminTeam);
      }

      set(selectedTeamsAtom, selectedTeams);
      set(isFetchingSelectedTeamsAtom, false);
    });

    fetchAtom.debugLabel = `fetchSelectedTeamsSetterAtomFamily(allowTeams: ${allowTeams})`;
    return fetchAtom;
  },
  isEqual,
);

function isEqual(a: Params, b: Params) {
  return (
    a.allowTeams === b.allowTeams &&
    a.currentAccountId === b.currentAccountId &&
    a.hasMultipleTeams == b.hasMultipleTeams &&
    a.account?.id === b.account?.id
  );
}
